import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from './stores/authStore';
import { useSettingsStore } from './stores/settingsStore';
import { useSessionStore } from './stores/sessionStore';
import { LoginScreen } from './components/auth/LoginScreen';
import { SessionStartScreen } from './components/import/SessionStartScreen';
import { TrainingScreen } from './components/training/TrainingScreen';
import { ShowdownScreen } from './components/showdown/ShowdownScreen';
import { DashboardScreen } from './components/dashboard/DashboardScreen';
import { ImportSettingsScreen } from './components/import/ImportSettingsScreen';
import { PlayersScreen } from './components/players/PlayersScreen';
import { AdminScreen } from './components/admin/AdminScreen';
import { SessionHistoryScreen } from './components/history/SessionHistoryScreen';
import { SessionDetailView } from './components/history/SessionDetailView';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ToastContainer } from './components/shared/ToastContainer';
import { KeyboardHelp } from './components/shared/KeyboardHelp';

type Screen = 'session-start' | 'training' | 'showdown' | 'dashboard' | 'players' | 'import-settings' | 'admin' | 'history' | 'session-detail';

export default function App() {
  const [screen, setScreen] = useState<Screen>('session-start');
  const [viewSessionId, setViewSessionId] = useState<string | null>(null);
  const load = useSettingsStore((s) => s.load);
  const resetSession = useSessionStore((s) => s.resetSession);
  const nextHand = useSessionStore((s) => s.nextHand);
  const phase = useSessionStore((s) => s.phase);

  const { user, role, loading: authLoading, init: initAuth, signOut } = useAuthStore();

  // Initialize Firebase Auth on mount
  useEffect(() => {
    initAuth();
  }, []);

  // Load settings once authenticated
  useEffect(() => {
    if (user) load();
  }, [user]);

  // Safety net: redirect to session-start if session fails while on training screen
  useEffect(() => {
    if (screen === 'training' && phase === 'IDLE') {
      setScreen('session-start');
    }
  }, [phase, screen]);

  // Guard navigation away from active training session
  const navigateTo = useCallback((target: Screen) => {
    const activePhase = useSessionStore.getState().phase;
    const isInSession = ['AWAITING_RANGE_UPDATE', 'REPLAYING', 'LOADING_HAND'].includes(activePhase);
    const leavingSession = (screen === 'training' || screen === 'showdown') && !['training', 'showdown', 'session-start'].includes(target);

    if (isInSession && leavingSession) {
      if (!window.confirm('You have an active training session. Leave and lose progress?')) {
        return;
      }
      resetSession();
    }
    setScreen(target);
  }, [screen, resetSession]);

  // Show loading spinner during auth check
  if (authLoading) {
    return (
      <div className="app-shell">
        <div className="placeholder-screen">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <LoginScreen />;
  }

  return (
    <div className="app-shell">
      <ToastContainer />
      <KeyboardHelp />
      <nav className="app-nav">
        <div className="app-nav-left">
        </div>
        <div className="app-nav-center">
          <span className="app-logo">&#9824; Range Reader</span>
        </div>
        <div className="app-nav-right">
          <button
            className={`nav-btn ${['session-start','training','showdown'].includes(screen) ? 'active' : ''}`}
            onClick={() => { resetSession(); setScreen('session-start'); }}
          >Play</button>
          <button
            className={`nav-btn ${screen === 'dashboard' ? 'active' : ''}`}
            onClick={() => navigateTo('dashboard')}
          >Stats</button>
          <button
            className={`nav-btn ${['history', 'session-detail'].includes(screen) ? 'active' : ''}`}
            onClick={() => navigateTo('history')}
          >History</button>
          <button
            className={`nav-btn ${screen === 'players' ? 'active' : ''}`}
            onClick={() => navigateTo('players')}
          >Players</button>
          <button
            className={`nav-btn ${screen === 'import-settings' ? 'active' : ''}`}
            onClick={() => navigateTo('import-settings')}
          >Import & Settings</button>
          {role === 'admin' && (
            <button
              className={`nav-btn ${screen === 'admin' ? 'active' : ''}`}
              onClick={() => navigateTo('admin')}
            >Admin</button>
          )}
          <button className="nav-btn" onClick={signOut}>Sign Out</button>
        </div>
      </nav>
      <main className="app-main">
        <ErrorBoundary>
          {screen === 'session-start' && (
            <SessionStartScreen onStartSession={() => setScreen('training')} />
          )}
          {screen === 'training' && (
            <TrainingScreen onShowdown={() => setScreen('showdown')} />
          )}
          {screen === 'showdown' && (
            <ShowdownScreen onNextHand={async () => {
              await nextHand();
              const newPhase = useSessionStore.getState().phase;
              if (newPhase === 'AWAITING_RANGE_UPDATE') setScreen('training');
              else if (newPhase === 'IDLE') setScreen('session-start');
            }} />
          )}
          {screen === 'dashboard' && <DashboardScreen />}
          {screen === 'history' && (
            <SessionHistoryScreen onViewSession={(id) => { setViewSessionId(id); setScreen('session-detail'); }} />
          )}
          {screen === 'session-detail' && viewSessionId && (
            <SessionDetailView sessionId={viewSessionId} onBack={() => setScreen('history')} />
          )}
          {screen === 'players' && <PlayersScreen />}
          {screen === 'import-settings' && <ImportSettingsScreen />}
          {screen === 'admin' && <AdminScreen />}
        </ErrorBoundary>
      </main>
    </div>
  );
}
